import React, { useState, useEffect, useRef, useCallback, memo } from "react";
import { createPortal } from "react-dom";
import { FaPaperclip, FaPaperPlane, FaStop } from "react-icons/fa";
import VoiceChat from "./VoiceChat";
import ElevenLabsVoice from "./ElevenLabsVoice";
import StreamModeToggle from "./StreamModeToggle";
import { ELEVENLABS_S2S_ENABLED, AVAILABLE_AI_MODELS, ALL_AI_MODELS, MODEL_DISPLAY_NAMES } from "../config/chatConfig";

// Memoized ChatInput Component with Local Input State
const ChatInput = memo(({
  onSend,
  isLoading,
  isMimicking,
  isCreatingSession,
  onStop,
  collapseSidebar,
  selectedImages = [],
  setSelectedImages,
  setUploadImages,
  handleImageUpload,
  user,
  isPopupEnabled,
  setIsPopupOpen,
  supportsImages,
  isLimitReached,
  isCentered = false,
  isMobile = false,
  isTablet = false,
  onAddMessage, // Add prop for voice transcript display
  currentSessionId, // For voice chat session
  navigate, // For voice chat navigation
  refreshSessions, // For voice chat sidebar refresh
  selectedModel,
  setSelectedModel
}) => {
  const textareaRef = useRef(null);
  const [input, setInput] = useState("");
  const [isIOS, setIsIOS] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const isMountedRef = useRef(true);
  
  // Detect iOS on mount and handle component lifecycle
  useEffect(() => {
    isMountedRef.current = true;

    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
                       (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    setIsIOS(isIOSDevice);

    if (isIOSDevice) {
      document.body.classList.add('ios-device');
    }

    return () => {
      isMountedRef.current = false;
      document.body.classList.remove('ios-device');
    };
  }, []);

  // Fix iOS Safari keyboard dismiss void space bug
  useEffect(() => {
    if (!window.visualViewport) return;

    let lastHeight = window.visualViewport.height;

    const handleViewportResize = () => {
      const currentHeight = window.visualViewport.height;
      // Keyboard closed - viewport height increased significantly
      if (currentHeight > lastHeight + 100) {
        // Force scroll to eliminate void space
        window.scrollTo(0, window.scrollY);
        // Also try resetting body scroll position
        document.body.style.height = '100%';
        requestAnimationFrame(() => {
          document.body.style.height = '';
        });
      }
      lastHeight = currentHeight;
    };

    window.visualViewport.addEventListener('resize', handleViewportResize);

    return () => {
      window.visualViewport.removeEventListener('resize', handleViewportResize);
    };
  }, []);

  const handleTextareaChange = useCallback((e) => {
    setInput(e.target.value);
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 320) + "px";
    }
  }, []);

  const handleSend = useCallback(() => {
    if (!input.trim() && selectedImages.length === 0) return; // Prevent empty sends

    const messageToSend = input.trim();
    setInput(""); // Clear input immediately
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto"; // Reset height only
    }
    onSend(messageToSend); // Trigger send after clearing
  }, [input, selectedImages, onSend]);

  // Add ref to access VoiceChat methods and track voice session state
  const voiceChatRef = useRef(null);
  const elevenLabsVoiceRef = useRef(null);
  const [isVoiceSessionActive, setIsVoiceSessionActive] = useState(false);
  const [isElevenLabsSessionActive, setIsElevenLabsSessionActive] = useState(false);

  // Check if user is admin/owner for ElevenLabs button
  const isAdminOrOwner = ['owner', 'admin'].includes(user?.['custom:membership']);
  const showElevenLabsButton = ELEVENLABS_S2S_ENABLED && isAdminOrOwner;

  const handleButtonClick = useCallback((e) => {
    if (isLoading || isMimicking) {
      if (process.env.NODE_ENV === 'development') {
        console.log("Stopping generation...");
      }
      onStop();
    } else if (input.trim() || selectedImages.length > 0) {
      // Text mode - send message
      handleSend();
    } else {
      // Voice mode - trigger voice chat
      if (voiceChatRef.current?.toggleVoiceChat) {
        voiceChatRef.current.toggleVoiceChat();
      }
    }
  }, [isLoading, isMimicking, onStop, handleSend, input, selectedImages]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault(); // Prevent newline
      if (isCreatingSession) {
        return; // Prevent submission while creating session
      }
      if (isLoading || isMimicking) {
        if (process.env.NODE_ENV === 'development') {
          console.log("Stopping generation...");
        }
        onStop();
      } else {
        handleSend();
      }
    }
  }, [isLoading, isMimicking, isCreatingSession, onStop, handleSend]);

  // Drag and drop handlers
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (supportsImages && !isLimitReached) {
      setIsDragOver(true);
    }
  }, [supportsImages, isLimitReached]);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    if (!user && isPopupEnabled) {
      setIsPopupOpen(true);
      return;
    }

    if (isLimitReached || !supportsImages) {
      return;
    }

    const files = Array.from(e.dataTransfer.files);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));

    if (imageFiles.length > 0) {
      // Create a synthetic event object for handleImageUpload with all images
      const syntheticEvent = {
        target: {
          files: imageFiles
        }
      };
      handleImageUpload(syntheticEvent);
    }
  }, [user, isPopupEnabled, setIsPopupOpen, isLimitReached, supportsImages, handleImageUpload]);
  
  // iOS-specific focusing method - currently unused but kept for potential future use
  // const focusTextarea = useCallback(() => {
  //   if (isMountedRef.current && textareaRef.current) {
  //     try {
  //       textareaRef.current.blur();
        
  //       setTimeout(() => {
  //         if (isMountedRef.current && textareaRef.current) {
  //           try {
  //             textareaRef.current.focus();
  //           } catch (err) {
  //             console.log("Focus error handled safely:", err.message);
  //           }
  //         }
  //       }, 50);
  //     } catch (err) {
  //       console.log("Blur error handled safely:", err.message);
  //     }
  //   }
    
  //   if (typeof collapseSidebar === 'function') {
  //     try {
  //       collapseSidebar();
  //     } catch (err) {
  //       console.log("Sidebar collapse error handled safely:", err.message);
  //     }
  //   }
  // }, [collapseSidebar]);

  return (
    <div className={`pb-0 pt-2 -mb-0.5 ${isCentered ? 'mx-6' : 'mx-0'} sm:mx-2.5`}
    >
      <form className="w-full flex gap-1.5 max-w-full sm:max-w-[800px] mx-auto" onSubmit={(e) => { e.preventDefault(); }}>
        <div className={`chatty-glow-wrapper flex-1 relative ${isCentered ? 'is-centered-glow' : ''} ${isLoading ? 'loading' : ''}`}
          style={{
            '--glow-br': isCentered
              ? 'var(--radius-3xl)'
              : isMobile
                ? 'var(--radius-3xl) var(--radius-3xl) 0 0'
                : 'var(--radius-3xl)',
            borderRadius: 'var(--glow-br)',
          }}
        >
        <div className={`chatty-input-box flex flex-col relative w-full transition overflow-hidden ${isCentered ? 'pb-2' : 'pb-4'} ${isIOS ? 'ios-input' : ''} ${isDragOver ? 'ring-2 ring-blue-400 bg-blue-50/20' : ''}`}
          style={{
            borderRadius: 'inherit',
            backgroundColor: 'var(--chatinput-bg, #1E1C1B)'
          }}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          
          {/* Content container */}
          <div className="relative z-10 flex flex-col w-full px-1">
          
          {/* Drag and drop overlay */}
          {isDragOver && (
            <div className="absolute inset-0 z-20 flex items-center justify-center bg-blue-500/10 rounded-3xl border-2 border-dashed border-blue-400">
              <div className="text-blue-600 font-medium">Drop image here</div>
            </div>
          )}
          
          {/* Hidden file input - supports multiple */}
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={(e) => {
              if (!user && isPopupEnabled) {
                e.target.value = null;
                setIsPopupOpen(true);
                return;
              }
              if (isLimitReached) {
                e.target.value = null;
                alert("Message limit reached. Please upgrade your plan or wait for the next billing cycle.");
                return;
              }
              handleImageUpload(e);
            }}
            id="image-upload"
            className="hidden"
            disabled={isLimitReached || isVoiceSessionActive || selectedImages.length >= 4}
          />

          {/* Multiple image previews */}
          {selectedImages.length > 0 && (
            <div className="flex gap-2 p-2 flex-wrap z-10">
              {selectedImages.map((img, index) => (
                <div key={index} className="relative w-20 h-20">
                  <img
                    src={img}
                    alt={`Selected ${index + 1}`}
                    className="w-20 h-20 rounded-lg object-cover shadow-md"
                  />
                  <button
                    type="button"
                    className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center shadow-md hover:bg-red-700 transition"
                    style={{ fontSize: '10px', lineHeight: 1 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      // Remove this specific image
                      setSelectedImages(prev => {
                        const updated = prev.filter((_, i) => i !== index);
                        localStorage.setItem('chatty_selectedImages', JSON.stringify(updated));
                        return updated;
                      });
                      setUploadImages(prev => {
                        const updated = prev.filter((_, i) => i !== index);
                        const existingData = JSON.parse(localStorage.getItem('chatty_uploadImages') || '[]');
                        localStorage.setItem('chatty_uploadImages', JSON.stringify(existingData.filter((_, i) => i !== index)));
                        return updated;
                      });
                    }}
                    disabled={isLimitReached}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
          
          {/* Text Input Area */}
          <div className="px-2.5 pt-2">
            <textarea
              ref={textareaRef}
              value={input}
              onChange={handleTextareaChange}
              onKeyDown={handleKeyDown}
              onFocus={collapseSidebar}
              onBlur={() => {
                // Fix iOS Safari keyboard dismiss void space
                if (isIOS) {
                  setTimeout(() => {
                    window.scrollTo(0, window.scrollY);
                  }, 100);
                }
              }}
              placeholder={isLimitReached ? "Message limit reached" : isVoiceSessionActive ? "Voice chat active..." : "Ask Chatty something..."}
              className={`scrollbar-hidden bg-transparent outline-hidden w-full pt-2 px-1 resize-none text-base ${isIOS ? 'ios-textarea' : ''} ${isVoiceSessionActive ? 'opacity-50' : ''}`}
              style={{
                minHeight: "2.5rem",
                maxHeight: "320px",
                color: 'var(--text-primary)',
                WebkitAppearance: isIOS ? 'none' : null,
                WebkitUserSelect: isIOS ? 'text' : null,
                touchAction: isIOS ? 'manipulation' : null
              }}
              disabled={isLimitReached || isVoiceSessionActive || isCreatingSession}
              rows={1}
            />
          </div>

          {/* Bottom Controls Bar */}
          <div className="flex justify-between items-end mt-0.5 mb-0 mx-4">
            {/* Left side controls */}
            <div className="flex items-center gap-1">
              {/* Attachment button */}
              {supportsImages && (
                <label
                  htmlFor={selectedImages.length >= 4 ? undefined : "image-upload"}
                  className={`hover:scale-105 transition-all duration-200 rounded-full p-2.5 outline-hidden focus:outline-hidden inline-flex items-center justify-center chatty-glass-button
                    ${isLimitReached || isVoiceSessionActive || selectedImages.length >= 4 ? "opacity-30 cursor-not-allowed" : "cursor-pointer"}`}
                >
                  <FaPaperclip size={12} style={{ width: '12px', height: '12px' }} />
                </label>
              )}
            </div>

            {/* Right side controls */}
            <div className="flex items-center gap-1">
              {/* Model selector popup */}
              {(() => {
                const isAdminOrOwner = ['owner', 'admin'].includes(user?.['custom:membership']);
                const showSelector = process.env.NODE_ENV === 'development' || isAdminOrOwner;
                const modelsToShow = isAdminOrOwner ? ALL_AI_MODELS : AVAILABLE_AI_MODELS;
                if (!showSelector || !selectedModel || Object.keys(modelsToShow).length <= 1) return null;
                return <ModelSelector selectedModel={selectedModel} setSelectedModel={setSelectedModel} modelsToShow={modelsToShow} />;
              })()}
              {/* Stream mode toggle — admin/owner only */}
              <StreamModeToggle user={user} />
              {/* ElevenLabs Voice button - Admin/Owner only */}
              {showElevenLabsButton && (
                <button
                  className={`transition-all duration-200 rounded-full p-2.5 self-center hover:scale-105 chatty-glass-button ${
                    isElevenLabsSessionActive ? 'ring-2 ring-purple-500' : ''
                  }`}
                  style={{
                    color: '#8b5cf6', // Purple for ElevenLabs
                    opacity: (isVoiceSessionActive || isCreatingSession) ? 0.5 : 1
                  }}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (elevenLabsVoiceRef.current?.toggleVoiceChat) {
                      elevenLabsVoiceRef.current.toggleVoiceChat();
                    }
                  }}
                  disabled={isLimitReached || isCreatingSession || isVoiceSessionActive}
                  title="ElevenLabs Voice (Admin)"
                >
                  <ElevenLabsVoice
                    ref={elevenLabsVoiceRef}
                    user={user}
                    size={12}
                    currentSessionId={currentSessionId}
                    navigate={navigate}
                    refreshSessions={refreshSessions}
                    onVoiceSessionChange={setIsElevenLabsSessionActive}
                    onTranscriptUpdate={(message) => {
                      if (message && message.text && onAddMessage) {
                        onAddMessage(message);
                        if (process.env.NODE_ENV === 'development') {
                          console.log('11Labs: Voice transcript added to UI:', message);
                        }
                      }
                    }}
                  />
                </button>
              )}

              {/* Send button (OpenAI Voice / Text) */}
              <div className="flex items-center">
                <button
                  className={`transition-all duration-200 rounded-full p-2.5 self-center ${
                    (input.trim() || selectedImages.length > 0)
                      ? 'hover:scale-105 chatty-send-button-active'
                      : 'hover:scale-105 chatty-glass-button'
                  } ${isCreatingSession ? 'opacity-50 cursor-wait' : ''}`}
                  style={{
                    color: 'var(--text-primary)',
                    backdropFilter: (input.trim() || selectedImages.length > 0) ? 'blur(8px)' : undefined,
                    WebkitBackdropFilter: (input.trim() || selectedImages.length > 0) ? 'blur(8px)' : undefined,
                    opacity: isCreatingSession ? 0.5 : 1,
                    backgroundColor: undefined
                  }}
                  type="submit"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleButtonClick(e);
                  }}
                  disabled={isLimitReached || isCreatingSession || isElevenLabsSessionActive}
                >
                  {isCreatingSession ? (
                    <svg className="animate-spin" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
                      <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round" />
                    </svg>
                  ) : isLoading || isMimicking ? (
                    <FaStop size={12} style={{ width: '12px', height: '12px' }} />
                  ) : (input.trim() || selectedImages.length > 0) && !isVoiceSessionActive ? (
                    <FaPaperPlane size={12} style={{ width: '12px', height: '12px' }} />
                  ) : (
                    <VoiceChat
                      ref={voiceChatRef}
                      user={user}
                      size={12}
                      currentSessionId={currentSessionId}
                      navigate={navigate}
                      refreshSessions={refreshSessions}
                      onVoiceSessionChange={setIsVoiceSessionActive}
                      onTranscriptUpdate={(message) => {
                        // Add voice transcripts to UI messages (same as text chat)
                        if (message && message.text && onAddMessage) {
                          // Use onAddMessage to add to chat UI
                          onAddMessage(message);
                          if (process.env.NODE_ENV === 'development') {
                            console.log('OpenAI: Voice transcript added to UI:', message);
                          }
                        }
                      }}
                    />
                  )}
                </button>
              </div>
            </div>
          </div>
          </div>
        </div>
        </div>
      </form>
      {/* Disclaimer - hide on mobile/tablet when chat is active (not centered) */}
      {((!isMobile && !isTablet) || isCentered) && (
        <p className="text-center text-xs mt-2 px-2" style={{ color: 'var(--text-secondary)', opacity: 0.7 }}>
          Chatty can make mistakes, by messaging to the AI you agree to our <a href="/terms" className="underline hover:opacity-80">Terms</a> and have read our <a href="/privacy" className="underline hover:opacity-80">Privacy Policy</a>.
        </p>
      )}
    </div>
  );
});

// Model selector popup — portal to escape overflow:hidden, positioned from button
const ModelSelector = ({ selectedModel, setSelectedModel, modelsToShow }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const popupRef = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (popupRef.current && !popupRef.current.contains(e.target) &&
          btnRef.current && !btnRef.current.contains(e.target)) setIsOpen(false);
    };
    if (isOpen) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  const toggle = () => {
    if (!isOpen && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setPos({ bottom: window.innerHeight - rect.top + 8, right: window.innerWidth - rect.right });
    }
    setIsOpen(!isOpen);
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={toggle}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          padding: '6px 8px',
          borderRadius: '8px',
          fontSize: '12px',
          color: 'var(--text-secondary)',
          backgroundColor: 'transparent',
          border: 'none',
          cursor: 'pointer',
        }}
        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-secondary, rgba(255,255,255,0.1))'}
        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
      >
        {MODEL_DISPLAY_NAMES[selectedModel] || selectedModel}
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {isOpen && pos && createPortal(
        <div
          ref={popupRef}
          style={{
            position: 'fixed',
            bottom: pos.bottom,
            right: pos.right,
            backgroundColor: 'var(--bg-primary, #1a1a1a)',
            border: '1px solid var(--border-color, rgba(255,255,255,0.1))',
            borderRadius: '12px',
            boxShadow: '0 -4px 16px rgba(0,0,0,0.3)',
            overflowY: 'auto',
            zIndex: 9999,
            minWidth: '180px',
            maxHeight: '300px',
          }}
        >
          {Object.keys(modelsToShow).map((model) => (
            <button
              key={model}
              type="button"
              onClick={() => { setSelectedModel(model); setIsOpen(false); }}
              style={{
                width: '100%',
                padding: '10px 16px',
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: model === selectedModel ? '#3b82f6' : 'var(--text-primary)',
                fontSize: '13px',
                fontWeight: model === selectedModel ? '600' : '400',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                textAlign: 'left',
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-secondary, rgba(255,255,255,0.1))'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              {MODEL_DISPLAY_NAMES[model] || model}
              {model === selectedModel && <span style={{ color: '#3b82f6' }}>✓</span>}
            </button>
          ))}
        </div>,
        document.body
      )}
    </>
  );
};

export default ChatInput;